import {
  Award,
  BookOpen,
  Building2,
  Calendar,
  ClipboardCheck,
  Contact,
  Database,
  FileSpreadsheet,
  FileText,
  FlaskConical,
  HelpCircle,
  LayoutDashboard,
  LifeBuoy,
  type LucideIcon,
  Mail,
  Newspaper,
  Unlock,
  Users,
} from "lucide-react";

import { effectiveAdminRole } from "@/lib/roles";

/** Section headers shown in the sidebar, in render order. Items with no
 * section (e.g. Dashboard) render standalone above the first group. */
export type AdminNavSection = "Programmes" | "Assessment" | "Content" | "Operations";

export const adminNavSections: AdminNavSection[] = [
  "Programmes",
  "Assessment",
  "Content",
  "Operations",
];

/** Roles that can reach the admin portal. Assessors get a minimal slice. */
export type AdminRole = "admin" | "assessor";

/** Which shared admin-scope controls the page's topbar shows.
 * "cohort" = cohort switcher only; "cohort+assignment" adds the W#A# slot
 * select. Omitted =
 * the page doesn't use the scope, so no controls render. */
export type AdminScopeKind = "cohort" | "cohort+assignment";

export interface AdminNavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  disabled: boolean;
  /** Sidebar section group; omitted = standalone at the top (Dashboard). */
  section?: AdminNavSection;
  /** Roles allowed to see this item. Omitted = admin-only. */
  roles?: AdminRole[];
  /** Shared cohort/assignment scope used by this page (drives the topbar switcher). */
  scope?: AdminScopeKind;
}

/**
 * Single source of truth for the admin portal's primary navigation. Consumed by
 * the sidebar (to render the grouped menu) and the layout/header (to derive the
 * current page title from the route), so labels never drift between the two.
 */
export const adminNavItems: AdminNavItem[] = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/admin/dashboard",
    disabled: false,
    scope: "cohort",
  },

  // Programmes — live cohort operations
  {
    icon: Calendar,
    label: "Programmes",
    path: "/admin/programmes",
    disabled: false,
    section: "Programmes",
  },
  {
    icon: FileText,
    label: "Assignments",
    path: "/admin/assignments",
    disabled: false,
    section: "Programmes",
    scope: "cohort+assignment",
  },
  {
    icon: Users,
    label: "Participants",
    path: "/admin/users",
    disabled: false,
    section: "Programmes",
  },

  // Assessment — grading & eval quality
  {
    icon: ClipboardCheck,
    label: "Reviews",
    path: "/admin/reviews",
    disabled: false,
    section: "Assessment",
    roles: ["admin", "assessor"],
    scope: "cohort+assignment",
  },
  {
    icon: FlaskConical,
    label: "Assessment Fixtures",
    path: "/admin/assessment-fixtures",
    disabled: false,
    section: "Assessment",
  },

  // Content — the topic-supply pipeline that feeds assignments
  { icon: BookOpen, label: "Topics", path: "/admin/topics", disabled: false, section: "Content" },
  { icon: Database, label: "Sources", path: "/admin/sources", disabled: false, section: "Content" },
  {
    icon: Newspaper,
    label: "Seed Events",
    path: "/admin/seed-events",
    disabled: false,
    section: "Content",
  },
  { icon: HelpCircle, label: "FAQ", path: "/admin/faqs", disabled: false, section: "Content" },
  { icon: Contact, label: "People", path: "/admin/people", disabled: false, section: "Content" },
  {
    icon: Unlock,
    label: "Unlock Screen",
    path: "/admin/unlock-screen",
    disabled: false,
    section: "Content",
  },

  // Operations — inbound, support, and future
  {
    icon: FileSpreadsheet,
    label: "Website Forms",
    path: "/admin/website-forms",
    disabled: false,
    section: "Operations",
  },
  {
    icon: LifeBuoy,
    label: "Support",
    path: "/admin/support",
    disabled: false,
    section: "Operations",
  },
  {
    icon: Mail,
    label: "Email Templates",
    path: "/admin/email-templates",
    disabled: false,
    section: "Operations",
  },
  {
    icon: Award,
    label: "Certificates",
    path: "/admin/certificates",
    disabled: false,
    section: "Operations",
    // Freemium Cohorts stay listed: they show their participation badges (KC-497).
    scope: "cohort",
  },
  {
    icon: Building2,
    label: "Organizations",
    path: "/admin/organizations",
    disabled: true,
    section: "Operations",
  },
];

/** Items with no section, in declared order (rendered standalone at the top). */
export const adminNavStandalone = adminNavItems.filter((item) => !item.section);

/** Items for a given section, in declared order. */
export function adminNavForSection(section: AdminNavSection): AdminNavItem[] {
  return adminNavItems.filter((item) => item.section === section);
}

/** Whether a role may see a nav item. Items without `roles` are admin-only. */
export function canRoleSeeNavItem(item: AdminNavItem, role: string | undefined): boolean {
  const allowed = item.roles ?? ["admin"];
  const effective = effectiveAdminRole(role);
  return effective !== undefined && (allowed as string[]).includes(effective);
}

/** Standalone items visible to the given role. */
export function adminNavStandaloneForRole(role: string | undefined): AdminNavItem[] {
  return adminNavStandalone.filter((item) => canRoleSeeNavItem(item, role));
}

/** Section items visible to the given role (empty if the role sees none). */
export function adminNavForSectionAndRole(
  section: AdminNavSection,
  role: string | undefined
): AdminNavItem[] {
  return adminNavForSection(section).filter((item) => canRoleSeeNavItem(item, role));
}

/**
 * Resolve the page title for the admin header from a pathname. Matches the
 * longest nav path that the current route starts with, so sub-routes
 * (e.g. /admin/programmes/:id/users) still resolve to "Programmes".
 */
function matchNavItem(pathname: string): AdminNavItem | undefined {
  return adminNavItems
    .filter((item) => pathname === item.path || pathname.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

export function getAdminPageTitle(pathname: string): string {
  return matchNavItem(pathname)?.label ?? "Admin";
}

/** Scope declared for the current route; undefined = no topbar scope controls.
 * Only the exact list route gets the switcher — detail sub-routes (e.g. a
 * single review) are pinned to their record and must not react to it. */
export function getAdminPageScope(pathname: string): AdminScopeKind | undefined {
  const item = matchNavItem(pathname);
  return item && pathname === item.path ? item.scope : undefined;
}
